import React, { useRef, useState } from 'react';
import type { SettingsFormState, VersionInfo, SettingsActivity } from './types';
import { AuthButton } from '@/core/permissions/AuthButton';
import { storageService } from '@/services/storage.service';
import { toast } from 'sonner';

interface SettingsFormProps {
  form: SettingsFormState;
  onChange: (patch: Partial<SettingsFormState>) => void;
  onSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  onSave: () => void;
  isSaving: boolean;
}

function formatDateTime(value?: string | null) {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('vi-VN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}

function inputClass() {
  return 'w-full h-9 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm font-medium text-slate-800 outline-none transition focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600';
}

function Section({ title, description, children, noBorder }: { title: string; description?: string; children: React.ReactNode; noBorder?: boolean }) {
  return (
    <section className={`p-4 sm:p-5 space-y-4 ${noBorder ? '' : 'border-b border-slate-100'}`}>
      <div>
        <h2 className="text-sm font-medium text-slate-900">{title}</h2>
        {description && <p className="text-xs text-slate-500 mt-0.5">{description}</p>}
      </div>
      {children}
    </section>
  );
}

function Toggle({ checked, onChange, label, description }: { checked: boolean; onChange: (value: boolean) => void; label: string; description?: string }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} className="flex w-full items-start gap-3 rounded-md p-2 text-left hover:bg-slate-50 border border-transparent transition">
      <span className={`mt-0.5 flex h-4 w-8 shrink-0 rounded-full p-0.5 transition ${checked ? 'bg-emerald-600' : 'bg-slate-300'}`}>
        <span className={`h-3 w-3 rounded-full bg-white shadow-sm transition ${checked ? 'translate-x-4' : ''}`} />
      </span>
      <span className="space-y-0.5">
        <span className="block text-sm font-medium text-slate-800">{label}</span>
        {description ? <span className="block text-xs text-slate-500">{description}</span> : null}
      </span>
    </button>
  );
}

export function SettingsForm({ form, onChange, onSubmit, onSave, isSaving }: SettingsFormProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isUploading, setIsUploading] = useState(false);
  const storagePercent = form.storageLimitGb > 0 ? Math.min(100, Math.round((form.storageUsedGb / form.storageLimitGb) * 100)) : 0;

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setIsUploading(true);
    const toastId = toast.loading('Đang tải ảnh lên...');

    try {
      const result = await storageService.uploadFile('media-assets', file, 'logos');
      if (result.error) throw new Error(result.error);

      onChange({ logoPath: result.url });
      toast.success('Tải ảnh lên thành công', { id: toastId });
    } catch (error) {
      console.error('[SettingsForm] logo upload error:', error);
      toast.error('Không thể tải ảnh lên', { id: toastId });
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const saveButton = (
    <AuthButton
      module="settings"
      action="update"
      behavior="disable"
      permissionReason="Bạn không có quyền thay đổi cài đặt hệ thống"
      type="submit"
      disabled={isSaving || isUploading}
      className="rounded-md bg-emerald-600 px-4 h-9 text-sm font-medium text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60 transition inline-flex items-center gap-2"
    >
      {isSaving ? <><i className="ri-loader-4-line animate-spin" /> Đang lưu...</> : <><i className="ri-save-3-line" /> Lưu cấu hình</>}
    </AuthButton>
  );

  return (
    <form id="settings-form" onSubmit={onSubmit} className="flex flex-col h-full">
      <Section title="Thông tin tổ chức" description="Thông tin định danh và logo của doanh nghiệp hiển thị trên hệ thống.">
        <div className="grid gap-5 lg:grid-cols-[1fr_200px_1fr]">
          <div className="space-y-4">
            <Field label="Tên hệ thống">
              <input className={inputClass()} value={form.systemName} onChange={(event) => onChange({ systemName: event.target.value })} />
            </Field>
            <Field label="Mô tả hệ thống">
              <textarea rows={3} className="w-full rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-800 outline-none transition focus:border-emerald-600 focus:ring-1 focus:ring-emerald-600" value={form.systemDescription} onChange={(event) => onChange({ systemDescription: event.target.value })} />
            </Field>
          </div>
          <div className="space-y-2">
            <span className="text-xs font-medium text-slate-700">Logo hệ thống</span>
            <div className="flex flex-col gap-2.5">
              <div className="h-24 w-full rounded-md border border-slate-200 bg-white p-2">
                {form.logoPath ? (
                  <img src={form.logoPath} alt="Logo hệ thống" className="h-full w-full object-contain" />
                ) : (
                  <div className="flex h-full w-full items-center justify-center rounded bg-slate-50 text-emerald-600 border border-dashed border-emerald-200">
                    <i className="ri-image-add-line text-2xl" />
                  </div>
                )}
              </div>
              <input type="file" ref={fileInputRef} onChange={handleLogoUpload} accept="image/*" className="hidden" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="inline-flex w-full h-8 items-center justify-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 text-xs font-medium text-slate-700 hover:bg-slate-50 transition disabled:opacity-50"
              >
                <i className={form.logoPath ? "ri-loop-left-line" : "ri-upload-2-line"} />
                {isUploading ? 'Đang xử lý...' : form.logoPath ? 'Thay đổi logo' : 'Tải ảnh lên'}
              </button>
            </div>
          </div>
          <div className="space-y-4">
            <Field label="Múi giờ">
              <select className={inputClass()} value={form.timezone} onChange={(event) => onChange({ timezone: event.target.value })}>
                <option>(UTC+07:00) Hanoi, Bangkok, Jakarta</option>
                <option>(UTC+08:00) Singapore, Kuala Lumpur</option>
                <option>(UTC+00:00) London</option>
              </select>
            </Field>
            <Field label="Định dạng ngày">
              <select className={inputClass()} value={form.dateFormat} onChange={(event) => onChange({ dateFormat: event.target.value })}>
                <option>DD/MM/YYYY</option>
                <option>MM/DD/YYYY</option>
                <option>YYYY-MM-DD</option>
              </select>
            </Field>
            <Field label="Ngôn ngữ">
              <select className={inputClass()} value={form.language} onChange={(event) => onChange({ language: event.target.value })}>
                <option>Tiếng Việt</option>
                <option>English</option>
              </select>
            </Field>
          </div>
        </div>
      </Section>



      <Section title="Lưu trữ & Tệp đính kèm" description="Quản lý dung lượng và các định dạng tệp tin cho phép trên toàn hệ thống.">
        <div className="grid gap-4 sm:grid-cols-3">
          <Field label="Dung lượng tối đa / tệp">
            <select className={inputClass()} value={form.maxFileSize} onChange={(event) => onChange({ maxFileSize: event.target.value })}>
              <option>25 MB</option>
              <option>50 MB</option>
              <option>100 MB</option>
            </select>
          </Field>
          <div className="space-y-1.5 sm:col-span-2">
            <span className="text-xs font-medium text-slate-700">Mức sử dụng dung lượng</span>
            <div className="flex h-9 items-center gap-3">
              <div className="flex-1 h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${storagePercent}%` }} />
              </div>
              <span className="text-xs font-medium text-slate-700 whitespace-nowrap">{form.storageUsedGb} / {form.storageLimitGb} GB</span>
            </div>
          </div>
          <div className="space-y-1.5 sm:col-span-3">
            <span className="text-xs font-medium text-slate-700">Định dạng tệp cho phép</span>
            <div className="flex flex-wrap gap-1.5">
              {form.allowedFileTypes.map((type) => <span key={type} className="rounded bg-slate-100 px-2 py-0.5 text-[11px] font-medium text-slate-600 border border-slate-200">{type}</span>)}
            </div>
          </div>
        </div>
      </Section>





      <div className="mt-auto border-t border-slate-200 bg-slate-50 p-4 sm:p-5 flex items-center justify-end gap-3 rounded-b-lg">
        {saveButton}
      </div>
    </form>
  );
}
